import React from 'react';
import { Loader2 } from 'lucide-react';
import { colors, spacing, typography, borders } from '../../../../tokens';
import { formatTokenAmount } from '../utils/formatters';

interface NetworkBalancesProps {
  polygonBalance: bigint | undefined;
  bscBalance: bigint | undefined;
  ethereumBalance: bigint | undefined;
  decimals: number;
  isLoading: boolean;
  currentChainId: number;
}

/**
 * Component to display FAR token balances across all networks
 * Highlights the current network for easy identification
 */
export const NetworkBalances: React.FC<NetworkBalancesProps> = ({
  polygonBalance,
  bscBalance,
  ethereumBalance,
  decimals,
  isLoading,
  currentChainId,
}) => {
  const networks = [
    { id: 137, name: 'Polygon', balance: polygonBalance },
    { id: 56, name: 'BSC', balance: bscBalance },
    { id: 1, name: 'Ethereum', balance: ethereumBalance },
  ];

  return (
    <div
      style={{
        padding: spacing.lg,
        backgroundColor: colors.background.secondary.default,
        borderRadius: borders.component.card.radius,
        border: `1px solid ${colors.border.default.default}`,
      }}
    >
      <div
        style={{
          fontSize: typography.fontSize.sm,
          fontWeight: typography.fontWeight.semibold,
          color: colors.text.secondary.default,
          marginBottom: spacing.md,
        }}
      >
        Your FAR Token Balances
      </div>

      <style>{`
        @media (max-width: 768px) {
          .network-balances-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
      <div
        className="network-balances-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: spacing.md,
        }}
      >
        {networks.map((network) => {
          const isCurrent = network.id === currentChainId;

          return (
            <div
              key={network.id}
              style={{
                padding: spacing.md,
                backgroundColor: isCurrent
                  ? colors.brand.primary.base
                  : colors.background.primary.default,
                borderRadius: borders.radius.md,
                border: `1px solid ${
                  isCurrent ? colors.brand.primary.base : colors.border.default.default
                }`,
                transition: 'all 0.2s ease',
              }}
            >
              <div
                style={{
                  fontSize: typography.fontSize.xs,
                  color: isCurrent ? colors.text.primary.default : colors.text.muted.default,
                  marginBottom: spacing.xs,
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.xs,
                }}
              >
                {network.name}
                {isCurrent && (
                  <span
                    style={{
                      fontSize: typography.fontSize.xs,
                      padding: '2px 6px',
                      backgroundColor: colors.background.primary.default,
                      borderRadius: borders.radius.xs,
                      color: colors.text.primary.default,
                    }}
                  >
                    Current
                  </span>
                )}
              </div>
              <div
                style={{
                  fontSize: typography.fontSize.lg,
                  fontWeight: typography.fontWeight.semibold,
                  color: isCurrent ? colors.text.primary.default : colors.text.primary.default,
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.xs,
                }}
              >
                {isLoading ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <>
                    {formatTokenAmount(network.balance, decimals)}
                    <span
                      style={{
                        fontSize: typography.fontSize.sm,
                        color: isCurrent
                          ? colors.text.secondary.default
                          : colors.text.muted.default,
                      }}
                    >
                      FAR
                    </span>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div
        style={{
          fontSize: typography.fontSize.xs,
          color: colors.text.muted.default,
          marginTop: spacing.md,
        }}
      >
        Balances are fetched from all networks regardless of your current connection
      </div>
    </div>
  );
};
